import React, { useEffect, useState } from "react";
import { environment } from "environment/environment";
import MerchantEmptyState from "./MerchantEmptyState";
import MerchantLoader from "./MerchantLoader";
import { isLiveMerchantPlatform } from "./merchantBranding";

interface CoverageArea {
  name: string;
  postalCodes: string[];
}

interface MerchantPricingService {
  id: string;
  name: string;
  description: string;
  cutoffTime: string | null;
  cutoffLabel: string;
  deliveryWindow: string | null;
  coverageTitle: string;
  coverageAreas: CoverageArea[];
  coverageNote?: string;
  source: "zulu_default" | "store_service";
}

const COVERAGE_PREVIEW = 2;

const coverageAreasOf = (service: MerchantPricingService): CoverageArea[] => {
  const areas = service.coverageAreas || [];
  return areas
    .map((area) => {
      if (typeof area === "string") {
        return { name: area, postalCodes: [] as string[] };
      }
      return {
        name: String(area.name || "").trim(),
        postalCodes: (area.postalCodes || []).filter(Boolean),
      };
    })
    .filter((area) => area.name || area.postalCodes.length > 0);
};

interface MerchantPricingSummary {
  mode?: "store_services" | "default_services";
  heading?: string;
  summary?: string;
  services?: MerchantPricingService[];
}

interface MerchantPricingTabProps {
  platform: string;
  shop: string;
  session: string;
}

const MerchantPricingTab: React.FC<MerchantPricingTabProps> = ({
  platform,
  shop,
  session,
}) => {
  const [data, setData] = useState<MerchantPricingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedCoverage, setExpandedCoverage] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!session || !isLiveMerchantPlatform(platform)) {
      setLoading(false);
      return;
    }
    const params = new URLSearchParams({ session });
    if (shop) {
      params.set("shop", shop);
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(`${environment.api_url}/api/v1/integrations/${platform}/pricing?${params.toString()}`)
      .then((response) => response.json())
      .then((body) => {
        if (cancelled) {
          return;
        }
        if (!body || body.success === false) {
          setError(body.error || body.message || "Could not load pricing.");
          setData(null);
          return;
        }
        setData((body.data || {}) as MerchantPricingSummary);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not reach the Zulu API. Make sure it is running on port 3015.");
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [platform, shop, session]);

  if (loading) {
    return <MerchantLoader size="panel" label="Loading pricing…" />;
  }

  if (error) {
    return (
      <MerchantEmptyState
        kind="error"
        title="Could not load pricing"
        description={error}
      />
    );
  }

  const services = data?.services || [];
  if (!services.length) {
    return (
      <article className="zulu-plugin__settings-card">
        <p className="zulu-plugin__settings-kicker">Pricing</p>
        <h2>No activated services</h2>
        <p className="zulu-plugin__settings-help">
          Zulu has not activated checkout services for this shop yet.
        </p>
      </article>
    );
  }

  return (
    <div className="zulu-plugin__pricing">
      <article className="zulu-plugin__settings-card">
        <p className="zulu-plugin__settings-kicker">Pricing</p>
        <h2>{data?.heading || "Activated services"}</h2>
        <p className="zulu-plugin__settings-help">
          {data?.summary || "These services are available at checkout for this shop."}
        </p>
      </article>
      {services.map((service) => {
        const areas = coverageAreasOf(service);
        const expanded = expandedCoverage[service.id] === true;
        const visibleAreas = expanded ? areas : areas.slice(0, COVERAGE_PREVIEW);
        const hiddenCount = areas.length - visibleAreas.length;
        return (
          <article key={service.id} className="zulu-plugin__pricing-card">
            <div className="zulu-plugin__pricing-card-head">
              <h3>{service.name}</h3>
              <span className="zulu-plugin__pricing-badge">Active</span>
            </div>
            {service.description ? <p>{service.description}</p> : null}
            <dl>
              <div>
                <dt>Cut-off time</dt>
                <dd>{service.cutoffLabel || "—"}</dd>
              </div>
              <div>
                <dt>Delivery window</dt>
                <dd>{service.deliveryWindow || "—"}</dd>
              </div>
              <div className="zulu-plugin__pricing-coverage">
                <dt>{service.coverageTitle || "Delivery coverage"}</dt>
                <dd>
                  {visibleAreas.map((area) => (
                    <div
                      key={`${area.name}:${area.postalCodes.join(",")}`}
                      className="zulu-plugin__pricing-area"
                    >
                      <strong>{area.name || "—"}</strong>
                      {area.postalCodes.length ? (
                        <span>{area.postalCodes.join(", ")}</span>
                      ) : null}
                    </div>
                  ))}
                  {!areas.length ? (
                    <span>{service.coverageNote || "—"}</span>
                  ) : null}
                  {areas.length > COVERAGE_PREVIEW ? (
                    <button
                      type="button"
                      className="zulu-plugin__pricing-more"
                      aria-expanded={expanded}
                      onClick={() =>
                        setExpandedCoverage((current) => ({
                          ...current,
                          [service.id]: !expanded,
                        }))
                      }
                    >
                      {expanded ? "See less" : `See more (${hiddenCount})`}
                    </button>
                  ) : null}
                </dd>
              </div>
            </dl>
          </article>
        );
      })}
    </div>
  );
};

export default MerchantPricingTab;
